import React, { useRef } from 'react'
import { createUseStyles } from 'react-jss'
import cn from 'classnames'
import isEmpty from 'lodash/isEmpty'
import ResponsiveImage from '../ResponsiveImage'
import theme from '../../style/theme'
import { span } from '../../style/span'
import Link from '../Link'
import detectIt from 'detect-it'
import { useHoverCursor } from '../Cursor'

/**
 * RecipeTile component - specialized tile for recipe sliders
 * Shows image with title below (not overlaid)
 */
const RecipeTile = ({ className, title, image, link, size }) => {
  const classes = useStyles()
  const ref = useRef()
  useHoverCursor(ref, !isEmpty(link))

  // Process image like Card component does - pass it directly to ResponsiveImage
  // console.log('RecipeTile received image:', image, 'for title:', title)

  // Define image aspect ratio like Card component
  const IMAGE_ASPECT = 500 / 700 // Same as our original aspect ratio

  // If we have a valid image object, use it directly like Card component
  const imageIsValid = image && (
    (image.asset && (image.asset._ref || image.asset._id)) ||
    image.src ||
    (image.sizes && image.sizes.length > 0)
  )

  // // console.log('Image is valid:', imageIsValid)

  // Process link to ensure proper navigation to recipe pages
  let processedLink = link
  if (link && link.url) {
    // Link is already in the correct format
    processedLink = link
  } else if (typeof link === 'string' && link !== '#') {
    // Convert string to link object format
    processedLink = {
      url: link.startsWith('/') ? link : `/${link}`
    }
  } else {
    processedLink = null
  }

  // console.log('RecipeTile - Processing link:', { original: link, processed: processedLink, title })
  return (
    <Link
      className={cn(classes.tile, { withLink: !isEmpty(processedLink) }, size)}
      nonLinkTag='div'
      link={processedLink}
      ref={ref}
    >
      <div className={classes.sliderTileWrapper}>
        {imageIsValid ? (
          <div className={classes.imageContainer}>
            <ResponsiveImage
              {...image}
              className={classes.image}
              aspect={IMAGE_ASPECT}
              disableLazy
            />
          </div>
        ) : (
          <div className={classes.placeholderImage}>
            <div className={classes.imagePlaceholder}>Recipe Image</div>
          </div>
        )}
        <div className={classes.content}>
          {title && <div className={classes.title}>{title}</div>}
        </div>
      </div>
    </Link>
  )
}

const useStyles = createUseStyles({
  tile: {
    background: 'transparent',
    display: 'block',
    whiteSpace: 'normal',
    color: theme.colors.textSecondary,
    width: span(10),
    position: 'relative',
    margin: [0, span(0), 0, 0],
    transform: 'rotate(0deg)', // No rotation for clean design
    overflow: 'hidden', // Ensure image corners are rounded
    cursor: 'pointer', // Indicate clickable
    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
    [theme.breakpoints.up('md')]: {
      width: span(7, 'md'), // Smaller width for 3/4 column layout
      margin: [0, span(0.1, 'md'), 0, span(0.1, 'md')] // Better spacing
    },
    [theme.breakpoints.up('lg')]: {
      width: span(6, 'md') // Even smaller for large screens in 3/4 column
    },
    ...(detectIt.primaryInput === 'touch' ? {} : {
      '&.withLink': {
        '&:hover': {
          transform: 'translateY(-2px)'
        },
        '& $image img': {
          transition: 'transform 1.6s ease-in-out'
        },
        '&:hover $image img': {
          transform: 'scale(1.05)' // Reduced scale for subtlety
        }
      }
    }),
    '&.large': {
      [theme.breakpoints.up('md')]: {
        width: span(11, 'md')
      }
    }
  },
  imageContainer: {
    overflow: 'hidden', // Ensure image hover effects don't overflow
    marginBottom: '12px', // Add space between image and title
    position: 'relative',
    paddingBottom: '140%', // Fixed aspect ratio of 500/700
    width: '100%',
    background: 'transparent', // Background color for images with transparency
    [theme.breakpoints.up('md')]: {
      marginBottom: '16px'
    },
    // Add styling for ResponsiveImage container
    '& > div': {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      '& img': {
        objectFit: 'cover',
        width: '100%',
        height: '100%'
      }
    }
  },
  sliderTileWrapper: {
    marginRight: '14px'
  },
  image: {
    '& span': {
      transform: 'translateZ(0)'
    }
  },
  placeholderImage: {
    width: '100%',
    height: 0,
    paddingBottom: '140%', // 500/700 aspect ratio (same as ResponsiveImage)
    background: '#f5f5f5',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    overflow: 'hidden',
    marginBottom: '12px'
  },
  imagePlaceholder: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    color: '#aaa',
    fontFamily: theme.fonts.body,
    fontSize: '16px',
    textAlign: 'center',
    width: '100%'
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    transform: 'translateZ(0)'
  },
  title: {
    fontFamily: theme.fonts.headings,
    fontWeight: theme.fonts.headingsFontWeight,
    textAlign: 'left',
    color: theme.colors.primary, // Using primary color for better visibility
    fontSize: '16px',
    lineHeight: 1.3,
    marginTop: '5px',
    [theme.breakpoints.up('md')]: {
      fontSize: '18px'
    }
  }
}, { name: 'RecipeTile' })

export default RecipeTile
